import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, SunMoon } from 'lucide-react';
import cn from 'classnames';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useTranslation } from 'react-i18next';
import Logo from '@components/shared/logo.tsx';
import LanguageSwitcher from '@components/shared/language-switcher/language-switcher.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import avatarLogo from '@assets/images/avatar.jpeg';
import { PATHS } from '../../../router/paths.ts';

interface SidebarShellProps {
  navSlot: ReactNode;
  bottomScrollSlot?: ReactNode;
  topSlot?: ReactNode;
  footerSlot?: ReactNode;
  /** When true, render the narrow icon rail variant (#2230). */
  collapsed?: boolean;
  /** Toggle between rail and full sidebar. Enables the chevron button. */
  onToggleCollapsed?: () => void;
}

export function SidebarShell({
  navSlot,
  bottomScrollSlot,
  topSlot,
  footerSlot,
  collapsed = false,
  onToggleCollapsed
}: SidebarShellProps) {
  const { getColour, theme, toggleTheme } = useThemeContext();
  const { t } = useTranslation();

  const shadow =
    theme === 'dark'
      ? '4px 0 12px -2px rgba(0,0,0,0.35), 2px 0 4px -1px rgba(0,0,0,0.20)'
      : '4px 0 12px -2px rgba(0,0,0,0.08), 2px 0 4px -1px rgba(0,0,0,0.05)';

  const toggleLabel = collapsed ? t('expand-sidebar') : t('collapse-sidebar');
  const ToggleIcon = collapsed ? ChevronRight : ChevronLeft;
  const toggleButton = onToggleCollapsed ? (
    <Tooltip label={toggleLabel} placement={collapsed ? 'right' : 'bottom'}>
      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-label={toggleLabel}
        aria-expanded={!collapsed}
        aria-controls="app-sidebar-nav"
        className="inline-flex h-8 w-8 items-center justify-center rounded-2xl transition-colors hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary"
        style={{ color: getColour('icon') }}
      >
        <ToggleIcon size={16} aria-hidden="true" />
      </button>
    </Tooltip>
  ) : null;

  return (
    <nav
      aria-label={t('main-navigation')}
      className={cn(
        'flex flex-col h-full relative z-30 transition-[width] duration-150 ease-out',
        collapsed ? 'w-14' : 'w-[16.5rem]'
      )}
      style={{ backgroundColor: getColour('sidebar'), boxShadow: shadow }}
    >
      {/* Logo header — height matches the shared EditorHeader row
          (`px-4 py-2 border-b` with h-9 content) so the sidebar's
          brand strip and the editor header sit exactly on the same
          horizontal line. Content is a 36px logo/avatar, padding is
          `py-2`, total ~52px — same as the header. */}
      <div
        className={cn(
          'shrink-0 flex items-center border-b py-2.5',
          collapsed ? 'justify-center px-2' : 'pl-5 pr-3'
        )}
        style={{
          backgroundColor: getColour('sidebar'),
          borderColor: getColour('border')
        }}
      >
        {collapsed ? (
          <Link
            to={PATHS().HOME}
            aria-label={t('home')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <img
              src={avatarLogo}
              alt=""
              className="h-9 w-9 object-cover rounded-2xl"
            />
          </Link>
        ) : (
          <>
            <Logo isLink imgClassName="h-9 w-auto" />
            <div className="ml-auto">{toggleButton}</div>
          </>
        )}
      </div>

      {/* Editor-mode chevron sits in its own thin strip directly
          under the logo. Only rendered when the parent supplied a
          toggle handler — i.e. editor routes. */}
      {collapsed && onToggleCollapsed && (
        <div className="shrink-0 flex flex-col items-center gap-1.5 py-1.5">
          {toggleButton}
          <div
            role="separator"
            aria-orientation="horizontal"
            className="h-px w-6"
            style={{ backgroundColor: getColour('hairline') }}
          />
        </div>
      )}

      {topSlot && !collapsed && (
        <div
          className="px-3 py-2 shrink-0"
          style={{ borderBottom: `1px solid ${getColour('hairline')}` }}
        >
          {topSlot}
        </div>
      )}

      {/* Scrollable nav + bottom content */}
      <div
        id="app-sidebar-nav"
        className="flex flex-1 min-h-0 flex-col relative z-10"
      >
        <div
          className={cn(
            'flex-1 min-h-0 overflow-y-auto',
            collapsed ? 'px-2 pt-3 pb-3' : 'px-3 pt-3 pb-4'
          )}
        >
          <div className="flex flex-col min-h-full">
            {navSlot}
            <div className="md:flex-1" />
            {!collapsed && bottomScrollSlot}
          </div>
        </div>

        {/* Footer: profile dropdown, then theme + language switcher at very bottom */}
        <div
          className={cn(
            'shrink-0 flex flex-col gap-2',
            collapsed ? 'px-2 py-2 items-center' : 'px-3 pb-3 pt-2'
          )}
          style={{ borderTop: `1px solid ${getColour('hairline')}` }}
        >
          {footerSlot}
          {!collapsed && (
            <>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleTheme(theme === 'light')}
                  className="flex flex-1 items-center justify-start gap-1.5 px-2 py-1.5 rounded-xl transition-all duration-200 hover:scale-105"
                  style={{
                    backgroundColor: getColour('secondLayer'),
                    border: `1px solid ${getColour('border')}`,
                    color: getColour('normalText')
                  }}
                  aria-label={t('dark-light-theme-switch')}
                >
                  <SunMoon size={14} style={{ color: getColour('icon') }} />
                  <span
                    className="text-[10px] font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {theme === 'light' ? t('light') : t('dark')}
                  </span>
                </button>
                <LanguageSwitcher fullWidth />
              </div>
              <Link
                to={PATHS().ACCESSIBILITY}
                className="text-[10px] text-center hover:underline"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-statement')}
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
